import { Box, Tooltip } from 'pcln-design-system'
import PropTypes from 'prop-types'
import React from 'react'
import Dropdown from '../Dropdown'
import TypeAheadInput from '../TypeAheadInput'

const TypeaheadItemPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  isPopularDestination: PropTypes.bool,
})

const propTypes = {
  borderRadius: PropTypes.string,
  dropDownStateCallback: PropTypes.func,
  dropdownOpen: PropTypes.bool,
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      type: PropTypes.string,
      isNearby: PropTypes.bool,
      isRecent: PropTypes.bool,
    })
  ),
  alwaysShowLabel: PropTypes.bool,
  debounce: PropTypes.number,
  initialValue: PropTypes.string,
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  ]),
  previousItemKeyCodes: PropTypes.arrayOf(PropTypes.number),
  nextItemKeyCodes: PropTypes.arrayOf(PropTypes.number),
  closeDropdownKeyCodes: PropTypes.arrayOf(PropTypes.number),
  label: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  placeholder: PropTypes.string,
  maxLength: PropTypes.string,
  onItemSelection: PropTypes.func.isRequired,
  onTypeAheadChange: PropTypes.func.isRequired,
  icon: PropTypes.node,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  errorMessage: PropTypes.string,
  isUsingErrorMessage: PropTypes.bool,
  inputProps: PropTypes.object,
  selectOnClick: PropTypes.bool,
  onInputClick: PropTypes.func,
  clearInputOnClick: PropTypes.bool,
  allowEmptySelection: PropTypes.bool,
  refillPreviousTypeaheadWhenEmpty: PropTypes.bool,
  nearbyHeading: PropTypes.string,
  recentHeading: PropTypes.string,
  showCurrentLocation: PropTypes.bool,
  currentLocation: PropTypes.shape({
    type: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }),
  popularDestItems: PropTypes.arrayOf(TypeaheadItemPropType),
  isUsingPopOver: PropTypes.bool,
  handleCurrentLocationPromise: PropTypes.func,
  isMobile: PropTypes.bool,
  isCurrentLocationDisabled: PropTypes.bool,
  onError: PropTypes.func,
  render: PropTypes.func,
  dropdownItemIconProps: PropTypes.object,
  dropdownItemTextProps: PropTypes.object,
  iconMap: PropTypes.object,
}

const noop = () => {}

const defaultProps = {
  borderRadius: 'xl',
  dropdownItems: [],
  width: 1,
  maxLength: '60',
  closeDropdownKeyCodes: [27],
  previousItemKeyCodes: [38],
  nextItemKeyCodes: [40],
  isUsingErrorMessage: true,
  inputProps: {},
  allowEmptySelection: false,
  isUsingPopOver: false,
  handleCurrentLocationPromise: noop,
  isCurrentLocationDisabled: false,
  onError: noop,
  clearInputOnClick: false,
  refillPreviousTypeaheadWhenEmpty: false,
}

class TypeAheadContainer extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      dropdownOpen: this.props.dropdownOpen,
      typeAheadContent: this.props.initialValue || '',
      previousTypeAheadContent: this.props.initialValue || '',
      highlightedIndex: this.props.allowEmptySelection ? -1 : 0,
      activeDescendant: null,
      isCurrentLocationDisabled: this.props.isCurrentLocationDisabled,
      isCurrentLocationTimedOut: false,
      currentLocation: this.props.currentLocation,
    }

    this.handleItemSelection = this.handleItemSelection.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputClick = this.handleInputClick.bind(this)
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this)
    this.handleDropdownClick = this.handleDropdownClick.bind(this)
    this.closeDropdownFromOutside = this.closeDropdownFromOutside.bind(this)
    this.closeDropdown = this.closeDropdown.bind(this)
    this.openDropdown = this.openDropdown.bind(this)
  }

  handleItemSelection(item, event) {
    if (item.isCurrentLocation) {
      if (this.state.isCurrentLocationDisabled) return

      this.setState({
        currentLocation: { content: 'Locating...', isCurrentLocation: true },
      })

      this.props
        .handleCurrentLocationPromise()
        .then((data) => {
          item = data || {}
          this.setState({
            dropdownOpen: false,
            typeAheadContent: item.displayNameAfterSelection || item.content || item.itemName || '',
            isCurrentLocationTimedOut: false,
            currentLocation: {
              content: 'Use Current Location',
              isCurrentLocation: true,
            },
          })
          this.props.onItemSelection(item, event)
          this.closeDropdown(this.props.isUsingPopOver)
        })
        .catch((error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              this.setState({
                isCurrentLocationTimedOut: false,
                isCurrentLocationDisabled: true,
                currentLocation: {
                  content: 'Location Sharing Disabled',
                  isCurrentLocation: true,
                },
              })
              break
            case error.UNSUPPORTED:
              this.setState({
                isCurrentLocationTimedOut: false,
                isCurrentLocationDisabled: true,
                currentLocation: {
                  content: 'Location Sharing Unsupported',
                  isCurrentLocation: true,
                },
              })
              break
            case error.TIMEOUT:
              window.alert(
                'Sorry, your location could not be determined. Please enter a destination in the field above.'
              )
              this.setState({
                isCurrentLocationTimedOut: true,
                currentLocation: {
                  content: 'Use Current Location',
                  isCurrentLocation: true,
                },
              })
              break
          }
        })
    } else {
      this.setState({
        dropdownOpen: false,
        typeAheadContent:
          item.displayNameAfterSelection || item.displayName || item.content || item.itemName || '',
      })
      this.props.onItemSelection(item, event)
      this.closeDropdown(this.props.isUsingPopOver)
    }
  }

  handleDropdownClick(item, event) {
    this.handleItemSelection(item, event)
  }

  focusInput() {
    const input = document.getElementById(this?.typeAheadRef?.inputRef?.props)
    if (input) {
      input.focus()
    }
  }

  handleInputChange(event) {
    event.persist()

    const highlightedIndex =
      event.target.value.length > 0 && this.state.dropdownOpen && this.props.dropdownItems.length > 0
        ? 0
        : this.props.allowEmptySelection
        ? -1
        : 0

    this.setState(
      {
        // Keep dropdown open while typing
        dropdownOpen: true,
        typeAheadContent: event.target.value,
        highlightedIndex,
      },
      () => {
        // Wait for callback to fire off parent event.
        // Optional Debounce
        const e = event
        if (this.props.debounce) {
          window.clearTimeout(this.debounceTypeahead)
          this.debounceTypeahead = window.setTimeout(() => {
            this.props.onTypeAheadChange(e)
          }, this.props.debounce)
        } else {
          this.props.onTypeAheadChange(e)
        }
      }
    )
  }

  handleInputKeyDown(event) {
    // All dropdown items will appear in this order in the dropdown
    // Nearby and recent will only appear if there are no "normal" items in the set
    // So this will exclusively be all nearby & recent, or all normal items
    const dropdownItemsInVisualOrder = [
      ...this.props.dropdownItems.filter(
        (item) =>
          !item.isNearby &&
          !item.isRecent &&
          !item.isPopularDestination &&
          item.type !== 'NON_SELECTABLE_CITY'
      ),
      ...this.props.dropdownItems.filter((item) => item.isPopularDestination),
      ...this.props.dropdownItems.filter((item) => item.isRecent),
      ...this.props.dropdownItems.filter((item) => item.isNearby),
    ]

    const currentHighlight = this.state.highlightedIndex > -1 ? this.state.highlightedIndex : 0

    if (this.props.nextItemKeyCodes.indexOf(event.keyCode) !== -1) {
      event.preventDefault()
      if (!this.state.dropdownOpen && dropdownItemsInVisualOrder.length > 0) {
        this.openDropdown()
      }
      if (currentHighlight < dropdownItemsInVisualOrder.length - 1) {
        this.setState({
          highlightedIndex: this.state.highlightedIndex + 1,
          activeDescendant: `${this.props.id}-dropdown-item-${this.state.highlightedIndex + 1}`,
          typeAheadContent: dropdownItemsInVisualOrder[this.state.highlightedIndex + 1].content,
        })
      }
    } else if (this.props.previousItemKeyCodes.indexOf(event.keyCode) !== -1 && this.state.dropdownOpen) {
      event.preventDefault()
      if (currentHighlight > 0) {
        this.setState({
          highlightedIndex: currentHighlight - 1,
          activeDescendant: `${this.props.id}-dropdown-item-${currentHighlight - 1}`,
          typeAheadContent: dropdownItemsInVisualOrder[currentHighlight - 1].content,
        })
      }
    } else if (event.keyCode === 13 && this.state.dropdownOpen) {
      event.preventDefault()
      // User hit enter, if highlighted index exists, select that one
      if (dropdownItemsInVisualOrder.length > 0) {
        this.closeDropdown()
        if (this.state.highlightedIndex > -1) {
          this.handleItemSelection(dropdownItemsInVisualOrder[currentHighlight], event)
        } else if (this.props.allowEmptySelection) {
          this.handleItemSelection({}, event)
        }
      }
    } else if (event.keyCode === 9 && this.state.dropdownOpen) {
      // User hit tab, if highlighted index exists, select that one
      if (dropdownItemsInVisualOrder.length > 0) {
        this.closeDropdown()
        if (this.state.highlightedIndex > -1) {
          this.handleItemSelection(dropdownItemsInVisualOrder[currentHighlight], event)
        } else if (this.props.allowEmptySelection) {
          this.handleItemSelection({}, event)
        }
      }
    } else if (this.props.closeDropdownKeyCodes.indexOf(event.keyCode) !== -1 && this.state.dropdownOpen) {
      // User pressed escape key
      // If we allow empty selection, fire off the event with empty object
      // This should notify the host app that the user did not select anything
      if (this.props.allowEmptySelection) {
        this.handleItemSelection({}, event)
      } else if (dropdownItemsInVisualOrder.length > 0) {
        // If we don't allow empty selection, either use the highlighted item
        this.handleItemSelection(dropdownItemsInVisualOrder[Math.max(currentHighlight, 0)], event)
      }
      this.closeDropdown()
      // TODO We may want to reset the highlighted item to the one that matches the one they've selected (if they've selected one) or
      // Clear the field entirely which seems to be ARIA recommendation
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Handle manual setting of `dropdownOpen` via props
    if (nextProps.dropdownOpen !== this.props.dropdownOpen) {
      if (nextProps.dropdownOpen) {
        this.addWindowListener()
      }
      this.setState({
        dropdownOpen: nextProps.dropdownOpen,
      })
    }

    if (nextProps.isCurrentLocationDisabled !== this.props.isCurrentLocationDisabled) {
      this.setState({
        isCurrentLocationDisabled: nextProps.isCurrentLocationDisabled,
      })
    }

    if (nextProps.currentLocation !== this.props.currentLocation) {
      this.setState({
        currentLocation: nextProps.currentLocation,
      })
    }

    if (nextProps.initialValue !== this.props.initialValue) {
      this.setState({
        typeAheadContent: nextProps.initialValue,
      })
    }
  }

  closeDropdown(flag = false) {
    if (this.state.dropdownOpen) {
      this.setState({
        dropdownOpen: false,
        activeDescendant: null,
      })
    }
    this.removeWindowListener()
    if (!flag) this.focusInput()
    window.setTimeout(() => {
      if (this.props.dropDownStateCallback) {
        this.props.dropDownStateCallback(false)
      }
    }, 1000)
  }

  openDropdown() {
    this.addWindowListener()

    // If we don't allow empty selection, always start with item 0 highlighted
    let highlightedIndex = this.props.allowEmptySelection ? -1 : 0

    // Only highlight index 0 if there is something in typeahead content
    if (this.state.typeAheadContent.length > 0) {
      highlightedIndex = this.state.highlightedIndex > -1 ? this.state.highlightedIndex : 0
    }

    this.setState({
      dropdownOpen: true,
      highlightedIndex,
      activeDescendant: highlightedIndex > -1 ? `${this.props.id}-dropdown-item-${highlightedIndex}` : null,
    })
  }

  handleInputClick(event) {
    this.setState((state) => ({
      previousTypeAheadContent: state.typeAheadContent,
    }))

    if (this.props.clearInputOnClick) {
      this.setState({ typeAheadContent: '' })
    }

    if (this.props.selectOnClick && event.target) {
      event.target.select()
    }

    if (this.props.onInputClick) {
      this.props.onInputClick(event)
    }
    this.openDropdown()
    if (this.props.dropDownStateCallback) {
      this.props.dropDownStateCallback(true)
    }
  }

  addWindowListener() {
    window.setTimeout(() => {
      document.body.addEventListener('click', this.closeDropdownFromOutside)
    }, 50)
  }

  removeWindowListener() {
    document.body.removeEventListener('click', this.closeDropdownFromOutside)
  }

  closeDropdownFromOutside(event) {
    let target = event.target
    while (target && target.parentNode) {
      if (target.parentNode === this.typeAheadWrapper) return
      target = target.parentNode
    }

    if (this.props.dropdownItems && this.props.dropdownItems.length && this.state.highlightedIndex > -1) {
      const dropdownItemsInVisualOrder = [
        ...this.props.dropdownItems.filter(
          (item) => !item.isNearby && !item.isRecent && item.type !== 'NON_SELECTABLE_CITY'
        ),
        ...this.props.dropdownItems.filter((item) => item.isRecent),
        ...this.props.dropdownItems.filter((item) => item.isNearby),
      ]
      this.handleItemSelection(dropdownItemsInVisualOrder[this.state.highlightedIndex], event)
    } else {
      if (this.props.refillPreviousTypeaheadWhenEmpty) {
        this.setState((state) => ({
          typeAheadContent: state.previousTypeAheadContent,
        }))
      }
      if (this.props.allowEmptySelection) {
        this.handleItemSelection({}, event)
      } else {
        this.setState({ dropdownOpen: false })
      }
    }

    this.removeWindowListener()
  }

  componentWillUnmount() {
    this.removeWindowListener()
  }

  render() {
    const {
      width,
      id,
      errorMessage,
      isUsingErrorMessage,
      placeholder,
      label,
      isMobile,
      alwaysShowLabel,
      maxLength,
      dropdownItems,
      nearbyHeading,
      recentHeading,
      showCurrentLocation,
      popularDestItems,
      render,
      iconMap,
    } = this.props

    const {
      dropdownOpen,
      highlightedIndex,
      typeAheadContent,
      isCurrentLocationDisabled,
      isCurrentLocationTimedOut,
      currentLocation,
    } = this.state

    const rootProps = {
      width,
      ref: (wrapper) => {
        this.typeAheadWrapper = wrapper
      },
      style: { position: 'relative' },
      color: 'background.lightest',
      borderRadius: this.props.borderRadius,
    }

    const inputProps = {
      id,
      placeholder,
      label,
      alwaysShowLabel,
      maxLength,
      tabIndex: 0,
      key: 'type-ahead-input',
      ref: (elem) => {
        this.typeAheadRef = elem
      },
      color: errorMessage ? 'red' : null,
      hasError: errorMessage,
      icon: this.props.icon,
      type: 'text',
      'aria-autocomplete': 'list',
      'aria-activedescendant': this.state.activeDescendant,
      'aria-describedby': errorMessage && isUsingErrorMessage ? `${id}-error` : null,
      'aria-invalid': !!errorMessage,
      'aria-labelledby': this.props['aria-labelledby'],
      'aria-label': this.props['aria-label'],
      value: typeAheadContent,
      onChange: this.handleInputChange,
      onKeyDown: this.handleInputKeyDown,
      onClick: this.handleInputClick,
      ...this.props.inputProps,
    }

    const errorProps = {
      errorMessage,
      isUsingErrorMessage,
      id: `${id}-error`,
      style: { zIndex: 3 },
      bottom: true,
      right: true,
      color: 'error',
      role: 'alert',
    }

    const dropdownProps = {
      nearbyHeading,
      recentHeading,
      width,
      isMobile,
      highlightedIndex,
      showCurrentLocation,
      currentLocation,
      isCurrentLocationDisabled,
      isCurrentLocationTimedOut,
      popularDestItems,
      key: 'type-ahead-dropdown',
      id: `${id}-dropdown`,
      searchItems: dropdownItems.filter(
        (item) => !item.isNearby && !item.isRecent && !item.isPopularDestination
      ),
      nearbyItems: dropdownItems.filter((item) => item.isNearby),
      recentItems: dropdownItems.filter((item) => item.isRecent),
      showDropdown: dropdownOpen,
      onClick: this.handleDropdownClick,
      onHandleSelectItem: this.handleItemSelection,
      dropdownItemIconProps: this.props.dropdownItemIconProps,
      dropdownItemTextProps: this.props.dropdownItemTextProps,
      iconMap,
    }

    const closeDropdown = this.closeDropdown

    if (typeof render === 'function') {
      return render({
        rootProps,
        inputProps,
        errorProps,
        dropdownProps,
        closeDropdown,
      })
    }

    return (
      <Box {...rootProps}>
        <TypeAheadInput
          {...inputProps}
          data-testid='typeahead-input'
          role='combobox'
          borderRadius={rootProps.borderRadius}
          aria-expanded={this.state.dropdownOpen ? 'true' : 'false'}
        />
        {errorProps.errorMessage && errorProps.isUsingErrorMessage && (
          <Tooltip {...errorProps}>{errorProps.errorMessage}</Tooltip>
        )}
        <Dropdown {...dropdownProps} />
      </Box>
    )
  }
}

TypeAheadContainer.propTypes = propTypes

TypeAheadContainer.defaultProps = defaultProps

export default TypeAheadContainer
